import type { ICommand } from '@model-framework/command';
import type { ModelCascadeRepository } from '@view-model/infrastructure/view-model/cascade/ModelCascadeRepository';
import type { StickyModelContents } from '@view-model/domain/model/model-contents/StickyModelContents';
import type { ViewModelId, ModelId, DBPath } from 'balus-common';
import { RTDBPath } from 'balus-common';
import { RefBuilder } from '@framework/repository';
import { DisplayOrderRepository } from '@model-framework/display-order/infrastructure';
import { DisplayOrderTree } from '@model-framework/display-order';
import type { ModelContentsDiff } from './computeModelContentsDiff';
import { computeModelContentsDiff } from './computeModelContentsDiff';

/**
 * モデルコンテンツの差分更新コマンド。
 * do/redoでは既存コンテンツとの差分のみをRTDBに書き込む。
 * undoでは元のコンテンツを全削除→全保存で確実に復元する。
 * ModelEntity自体は削除しない。
 */
export class ViewContentPatchCommand implements ICommand {
  constructor(
    private readonly modelCascadeRepository: ModelCascadeRepository,
    private readonly viewModelId: ViewModelId,
    private readonly oldContents: StickyModelContents,
    private readonly newContents: StickyModelContents
  ) {}

  /**
   * oldContents と newContents の差分を計算し、modelContentPath に対する単一の update で適用する。
   * display-order はノード/ゾーンの追加・削除から差分更新する。
   */
  do(): void {
    const diff = computeModelContentsDiff(this.oldContents.dump(), this.newContents.dump());
    const modelId = this.newContents.key.id;
    const modelContentPath = RTDBPath.Model.modelContentPath(this.viewModelId, modelId);

    const collectionEntries = buildCollectionEntries(this.viewModelId, modelId, diff);

    // 全コレクションの差分を modelContentPath からの相対パスで1つの update オブジェクトにまとめる
    const updates: Record<string, unknown> = {};
    for (const [collectionPath, { save, removeIds }] of collectionEntries) {
      const collectionName = relativePath(collectionPath, modelContentPath);
      for (const [id, value] of Object.entries(save)) {
        updates[`${collectionName}/${id}`] = value;
      }
      for (const id of removeIds) {
        updates[`${collectionName}/${id}`] = null;
      }
    }

    // ノード/ゾーン実体より先に並び順の変更が他クライアントから見えないように、
    // modelContent の更新完了後に display-order を更新する
    const contentUpdate =
      Object.keys(updates).length > 0 ? RefBuilder.ref(modelContentPath).update(updates) : Promise.resolve();
    contentUpdate.then(() => this.updateDisplayOrder(modelId));
  }

  // 全置換なので、AI 編集のあとに加えられた変更（コメントを含む）も巻き戻り、書き戻しの対象外のリアクションは編集前のものも消える。全置換の見直しは影響が広いため受容している。
  undo(): void {
    this.replaceContents(this.oldContents);
  }

  redo(): void {
    this.do();
  }

  // undo/redo は保持したコンテンツ全体を全置換で復元するため、対象モデルが存在すれば常に実行できる。
  async canUndo(): Promise<boolean> {
    const modelContents = await this.modelCascadeRepository.load(this.newContents.key.id);
    return !!modelContents;
  }

  async canRedo(): Promise<boolean> {
    const modelContents = await this.modelCascadeRepository.load(this.oldContents.key.id);
    return !!modelContents;
  }

  /**
   * display-order を newContents のツリーで置き換える。
   *
   * 要素の追加・削除だけでなくゾーン所属(親子関係)の変更も反映する必要があるため、
   * 追加・削除分の差分更新ではなくツリー全体をアトミックに置換する。
   */
  private updateDisplayOrder(modelId: ModelId): void {
    const oldTree = DisplayOrderTree.load(this.oldContents.dump().displayOrderTree);
    const newTree = DisplayOrderTree.load(this.newContents.dump().displayOrderTree);
    if (oldTree.isEqual(newTree)) return;

    const repo = new DisplayOrderRepository(this.viewModelId, modelId);
    repo.replaceTree(oldTree, newTree).then();
  }

  /**
   * コンテンツを全削除してから保持していた元のコンテンツを書き込み、確実に復元する。
   */
  private replaceContents(contents: StickyModelContents): void {
    const modelId = contents.key.id;
    this.modelCascadeRepository.deleteContents(modelId).then(() => {
      this.modelCascadeRepository.saveContents(contents).then();
    });
  }
}

/**
 * フルパスからベースパスを除いた相対パスを返す。
 * 両方のパスは RTDBPath から取得されるため、ベースパスがフルパスの接頭辞であることが保証されている。
 */
function relativePath(fullPath: DBPath, basePath: DBPath): string {
  return fullPath.substring(basePath.length + 1);
}

/**
 * RTDBPathの各コレクションパスとdiffフィールドの対応を構築する。
 * display-order はノード/ゾーンの追加・削除から差分更新するため含まない。
 */
function buildCollectionEntries(
  viewModelId: ViewModelId,
  modelId: ModelId,
  diff: ModelContentsDiff
): [DBPath, { save: Record<string, unknown>; removeIds: string[] }][] {
  return [
    [RTDBPath.Node.nodesPath(viewModelId, modelId), diff.nodes],
    [RTDBPath.Link.linksPath(viewModelId, modelId), diff.links],
    [RTDBPath.Zone.zonesPath(viewModelId, modelId), diff.zones],
    [RTDBPath.Zone.positionsPath(viewModelId, modelId), diff.zonePositions],
    [RTDBPath.DescriptionPanel.panelsPath(viewModelId, modelId), diff.descriptionPanels],
    [RTDBPath.DescriptionPanel.positionsPath(viewModelId, modelId), diff.descriptionPanelPositions],
    [RTDBPath.Node.descriptionsPath(viewModelId, modelId), diff.nodeDescriptions],
    [RTDBPath.Zone.descriptionsPath(viewModelId, modelId), diff.zoneDescriptions],
    [RTDBPath.AnnotationLink.linksPath(viewModelId, modelId), diff.annotationLinks],
  ];
}
